import React from 'react';
import { X, HelpCircle } from 'lucide-react';

const ExcelImportHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <HelpCircle className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              คู่มือการ Import ข้อมูลจาก Excel
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-code:text-gray-900 dark:prose-code:text-gray-100 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:text-gray-900 dark:prose-pre:text-gray-100 prose-table:border-gray-200 dark:prose-table:border-gray-700 prose-th:bg-gray-50 dark:prose-th:bg-gray-900 prose-th:text-gray-900 dark:prose-th:text-gray-100 prose-td:text-gray-700 dark:prose-td:text-gray-300 prose-hr:border-gray-200 dark:prose-hr:border-gray-700 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-400">
            <div className="excel-import-help" dangerouslySetInnerHTML={{ __html: helpContentHTML }} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ปิด
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// HTML content converted from markdown
const helpContentHTML = `
<style>
  .excel-import-help {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.6;
  }
  .excel-import-help h1 {
    font-size: 1.875rem;
    font-weight: 700;
    margin-top: 2rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }
  .excel-import-help h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  .excel-import-help h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }
  .excel-import-help h4 {
    font-size: 1.125rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  .excel-import-help ul, .excel-import-help ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }
  .excel-import-help li {
    margin-bottom: 0.5rem;
  }
  .excel-import-help table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }
  .excel-import-help th, .excel-import-help td {
    padding: 0.75rem;
    text-align: left;
  }
  .excel-import-help th {
    font-weight: 600;
  }
  .excel-import-help code {
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.875em;
  }
  .excel-import-help pre {
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1rem 0;
  }
  .excel-import-help hr {
    border: none;
    margin: 2rem 0;
  }
  .excel-import-help blockquote {
    border-left: 4px solid #3b82f6;
    padding-left: 1rem;
    margin: 1rem 0;
  }
  .excel-import-help p {
    margin-bottom: 1rem;
  }
</style>

<h1>คู่มือการ Import ข้อมูลจาก Excel</h1>

<h2 id="ภาพรวม">ภาพรวม</h2>
<p>ระบบ Import Excel ช่วยให้คุณสามารถนำเข้าข้อมูลลูกค้า (Customers) จากไฟล์ Excel (.xlsx หรือ .xls) ได้อย่างรวดเร็วและง่ายดาย โดยรองรับฟีเจอร์ต่างๆ ดังนี้:</p>
<ul>
  <li>✅ <strong>Auto-detection</strong>: ระบบจะพยายามจับคู่คอลัมน์อัตโนมัติ</li>
  <li>✅ <strong>Multi-field Mapping</strong>: Map 1 คอลัมน์ไปยังหลายฟิลด์พร้อมกัน</li>
  <li>✅ <strong>Multiple Values</strong>: รองรับ Email และ Phone หลายค่า</li>
  <li>✅ <strong>Validation</strong>: ตรวจสอบความถูกต้องของข้อมูลก่อน Import</li>
  <li>✅ <strong>Preview</strong>: ดูตัวอย่างข้อมูลก่อน Import จริง</li>
</ul>

<hr />

<h2 id="ขั้นตอนการ-import">ขั้นตอนการ Import</h2>

<h3>ขั้นที่ 1: อัปโหลดไฟล์</h3>
<ol>
  <li>เปิดหน้า Customers และคลิกปุ่ม <strong>Import Excel</strong></li>
  <li>ลากไฟล์ Excel มาวางในพื้นที่ Upload หรือคลิกเพื่อเลือกไฟล์</li>
  <li>ระบบจะอัปโหลดและอ่านข้อมูลอัตโนมัติ</li>
  <li>หากไฟล์มีหลาย Sheet ให้เลือก Sheet ที่ต้องการ</li>
</ol>
<p><strong>หมายเหตุ</strong>:</p>
<ul>
  <li>รองรับไฟล์ <code>.xlsx</code> และ <code>.xls</code> เท่านั้น</li>
  <li>ขนาดไฟล์ไม่เกิน 10MB</li>
  <li>แถวแรกจะถูกใช้เป็น Header อัตโนมัติ</li>
</ul>

<h3>ขั้นที่ 2: Map คอลัมน์</h3>
<ol>
  <li>ระบบจะแสดงคอลัมน์ทั้งหมดจากไฟล์ Excel</li>
  <li>เลือกฟิลด์ที่ต้องการ Map สำหรับแต่ละคอลัมน์</li>
  <li>คอลัมน์ที่ระบบจับคู่ได้อัตโนมัติจะมีป้าย <strong>Auto-detected</strong></li>
  <li>คลิก <strong>Validate & Preview</strong> เพื่อตรวจสอบข้อมูล</li>
</ol>

<h3>ขั้นที่ 3: ตรวจสอบและ Import</h3>
<ol>
  <li>ระบบจะแสดงตัวอย่างข้อมูลที่จะ Import</li>
  <li>ตรวจสอบข้อมูลว่าถูกต้องหรือไม่</li>
  <li>หากมีข้อผิดพลาด สามารถเลือก <strong>Skip error rows</strong> เพื่อข้ามแถวที่มีปัญหา</li>
  <li>คลิก <strong>Import Now</strong> เพื่อเริ่ม Import</li>
</ol>

<hr />

<h2 id="การ-map-คอลัมน์">การ Map คอลัมน์</h2>

<h3>ฟิลด์ที่สามารถ Map ได้</h3>
<p>ระบบแบ่งฟิลด์ออกเป็น 4 กลุ่ม:</p>
<ol>
  <li><strong>📋 Customer Fields</strong>: ฟิลด์ของ Customer (เช่น CustomerCode, Status, Priority)</li>
  <li><strong>👤 Person Fields</strong>: ฟิลด์ของบุคคล (เช่น FirstName, LastName, PersonEmail, PersonPhone)</li>
  <li><strong>🏢 Company Fields</strong>: ฟิลด์ของบริษัท (เช่น CompanyName, CompanyAddress, CompanyPhone)</li>
  <li><strong>⚙️ Other Fields</strong>: ฟิลด์อื่นๆ</li>
</ol>

<h3>ตัวอย่างการ Map</h3>
<table>
  <thead>
    <tr>
      <th>คอลัมน์ Excel</th>
      <th>ฟิลด์ที่ Map</th>
      <th>หมายเหตุ</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>ชื่อ</td>
      <td>FirstName</td>
      <td>Person Fields</td>
    </tr>
    <tr>
      <td>นามสกุล</td>
      <td>LastName</td>
      <td>Person Fields</td>
    </tr>
    <tr>
      <td>อีเมล</td>
      <td>PersonEmail</td>
      <td>Person Fields</td>
    </tr>
    <tr>
      <td>เบอร์โทร</td>
      <td>PersonPhone</td>
      <td>Person Fields</td>
    </tr>
    <tr>
      <td>ชื่อบริษัท</td>
      <td>CompanyName</td>
      <td>Company Fields</td>
    </tr>
    <tr>
      <td>ที่อยู่</td>
      <td>CompanyAddress</td>
      <td>Company Fields</td>
    </tr>
  </tbody>
</table>

<hr />

<h2 id="การ-map-หลายฟิลด์จากคอลัมน์เดียว">การ Map หลายฟิลด์จากคอลัมน์เดียว</h2>

<h3>ฟีเจอร์นี้ใช้สำหรับกรณีใด?</h3>
<p>เมื่อข้อมูลหลายฟิลด์อยู่ในคอลัมน์เดียว เช่น:</p>
<ul>
  <li><strong>ชื่อ-นามสกุล</strong>: "สมชาย ใจดี" → แยกเป็น FirstName และ LastName</li>
  <li><strong>ชื่อบริษัท (สาขา)</strong>: "บริษัท ABC (สาขากรุงเทพ)" → แยกเป็น CompanyName และ Branch</li>
</ul>

<h3>วิธีใช้งาน</h3>
<ol>
  <li>Map คอลัมน์ไปยังฟิลด์แรกที่ต้องการ</li>
  <li>คลิกปุ่ม <strong>⚡ Split to multiple fields</strong> ใต้ Dropdown</li>
  <li>เลือกฟิลด์เพิ่มเติมที่ต้องการ Map</li>
  <li>กำหนดวิธีการ Split:
    <ul>
      <li><strong>Split by Space</strong>: แยกด้วยช่องว่าง (เช่น "สมชาย ใจดี")</li>
      <li><strong>Split by Parentheses</strong>: แยกด้วยวงเล็บ (เช่น "บริษัท ABC (สาขากรุงเทพ)")</li>
      <li><strong>Custom Separator</strong>: แยกด้วยตัวคั่นที่กำหนดเอง (เช่น comma, dash)</li>
    </ul>
  </li>
</ol>

<h3>ตัวอย่างการใช้งาน</h3>

<h4>ตัวอย่างที่ 1: แยกชื่อ-นามสกุล</h4>
<p><strong>ข้อมูลใน Excel:</strong></p>
<pre><code>ชื่อ-นามสกุล
สมชาย ใจดี</code></pre>
<p><strong>การตั้งค่า:</strong></p>
<ul>
  <li>Field 1: <code>FirstName</code></li>
  <li>Field 2: <code>LastName</code></li>
  <li>Split Type: <code>Space</code></li>
</ul>
<p><strong>ผลลัพธ์:</strong></p>
<ul>
  <li>FirstName = "สมชาย"</li>
  <li>LastName = "ใจดี"</li>
</ul>

<h4>ตัวอย่างที่ 2: แยกชื่อบริษัทและสาขา</h4>
<p><strong>ข้อมูลใน Excel:</strong></p>
<pre><code>หน่วยงาน
บริษัท ABC (สาขากรุงเทพ)</code></pre>
<p><strong>การตั้งค่า:</strong></p>
<ul>
  <li>Field 1: <code>CompanyName</code></li>
  <li>Field 2: <code>Branch</code></li>
  <li>Split Type: <code>Parentheses</code></li>
</ul>
<p><strong>ผลลัพธ์:</strong></p>
<ul>
  <li>CompanyName = "บริษัท ABC"</li>
  <li>Branch = "สาขากรุงเทพ"</li>
</ul>

<h4>ตัวอย่างที่ 3: Custom Separator</h4>
<p><strong>ข้อมูลใน Excel:</strong></p>
<pre><code>ที่อยู่
123 ถนนสุขุมวิท, แขวงคลองตัน, เขตคลองตัน, กรุงเทพฯ</code></pre>
<p><strong>การตั้งค่า:</strong></p>
<ul>
  <li>Field 1: <code>CompanyAddress</code></li>
  <li>Field 2: <code>City</code></li>
  <li>Split Type: <code>Custom Separator</code></li>
  <li>Separator: <code>,</code></li>
</ul>
<p><strong>ผลลัพธ์:</strong></p>
<ul>
  <li>CompanyAddress = "123 ถนนสุขุมวิท"</li>
  <li>City = "แขวงคลองตัน, เขตคลองตัน, กรุงเทพฯ"</li>
</ul>

<hr />

<h2 id="การ-import-email-และ-phone-หลายค่า">การ Import Email และ Phone หลายค่า</h2>

<h3>ฟีเจอร์นี้ใช้สำหรับกรณีใด?</h3>
<p>เมื่อลูกค้ามี Email หรือ Phone หลายหมายเลข และต้องการเก็บทั้งหมดไว้ในระบบ</p>

<h3>รูปแบบที่รองรับ</h3>
<p>ระบบจะรับรู้และแปลงตัวคั่นต่างๆ เป็นรูปแบบมาตรฐาน (<code>;</code>) อัตโนมัติ:</p>
<ul>
  <li>✅ Comma: <code>email1@example.com, email2@example.com</code></li>
  <li>✅ Semicolon: <code>email1@example.com; email2@example.com</code></li>
  <li>✅ Pipe: <code>email1@example.com | email2@example.com</code></li>
  <li>✅ Newline: <code>email1@example.com\nemail2@example.com</code></li>
  <li>✅ Multiple spaces: <code>email1@example.com  email2@example.com</code></li>
</ul>

<h3>ตัวอย่างการใช้งาน</h3>

<h4>ตัวอย่างที่ 1: Email หลายค่า</h4>
<p><strong>ข้อมูลใน Excel:</strong></p>
<pre><code>อีเมล
john@example.com, jane@example.com</code></pre>
<p><strong>ผลลัพธ์ในระบบ:</strong></p>
<ul>
  <li>เก็บเป็น: <code>john@example.com;jane@example.com</code></li>
  <li>แสดงผล: แต่ละ Email สามารถคลิกเพื่อส่งอีเมลได้</li>
</ul>

<h4>ตัวอย่างที่ 2: Phone หลายหมายเลข</h4>
<p><strong>ข้อมูลใน Excel:</strong></p>
<pre><code>เบอร์โทร
02-123-4567; 081-234-5678</code></pre>
<p><strong>ผลลัพธ์ในระบบ:</strong></p>
<ul>
  <li>เก็บเป็น: <code>02-123-4567;081-234-5678</code></li>
  <li>แสดงผล: แสดงเป็น Badge แยกแต่ละหมายเลข</li>
</ul>

<h3>การ Validation</h3>
<ul>
  <li><strong>Email</strong>: ระบบจะตรวจสอบความถูกต้องของแต่ละ Email</li>
  <li><strong>Phone</strong>: ระบบจะตรวจสอบความถูกต้องของแต่ละ Phone และแสดง Warning หากไม่ถูกต้อง</li>
</ul>

<hr />

<h2 id="ตัวเลือกเพิ่มเติม">ตัวเลือกเพิ่มเติม</h2>

<h3>1. Upsert Keys (สำหรับ Customer เท่านั้น)</h3>
<p>ใช้สำหรับกำหนดว่าควรใช้ฟิลด์ใดในการตรวจสอบว่ามีข้อมูลอยู่แล้วในระบบ:</p>
<ul>
  <li><strong>Customer Code</strong>: ใช้รหัสลูกค้า</li>
  <li><strong>Email</strong>: ใช้ Email</li>
  <li><strong>First Name + Last Name</strong>: ใช้ชื่อ-นามสกุล</li>
  <li><strong>Company Name</strong>: ใช้ชื่อบริษัท</li>
  <li><strong>Phone</strong>: ใช้เบอร์โทร</li>
</ul>
<p><strong>การทำงาน:</strong></p>
<ul>
  <li>หากพบข้อมูลที่ตรงกัน → <strong>อัปเดต</strong> ข้อมูลเดิม</li>
  <li>หากไม่พบ → <strong>สร้าง</strong> ข้อมูลใหม่</li>
</ul>

<h3>2. Import Tags</h3>
<p><strong>Automatic Tags:</strong></p>
<ul>
  <li>ระบบจะเพิ่ม Tag อัตโนมัติ: <code>Excel - 2025-12-06T22:30:00Z</code></li>
  <li>Tag นี้จะถูกบันทึกใน Source column</li>
</ul>
<p><strong>Custom Tags:</strong></p>
<ul>
  <li>สามารถเพิ่ม Tag เพิ่มเติมได้ (คั่นด้วย comma)</li>
  <li>ตัวอย่าง: <code>Q4-2025, Trade Show, VIP Customers</code></li>
</ul>

<h3>3. Row Range Selection</h3>
<p><strong>Load All Rows:</strong></p>
<ul>
  <li>Import ทุกแถวใน Sheet</li>
</ul>
<p><strong>Load Specific Range:</strong></p>
<ul>
  <li>กำหนดแถวเริ่มต้น (Start Row)</li>
  <li>กำหนดแถวสิ้นสุด (End Row) หรือเว้นว่างเพื่อ Import ถึงท้ายไฟล์</li>
  <li><strong>หมายเหตุ</strong>: แถวที่ 1 คือ Header</li>
</ul>

<hr />

<h2 id="การแก้ไขปัญหา">การแก้ไขปัญหา</h2>

<h3>ปัญหาที่พบบ่อย</h3>

<h4>1. ไฟล์ไม่สามารถอัปโหลดได้</h4>
<p><strong>สาเหตุ:</strong></p>
<ul>
  <li>ไฟล์ไม่ใช่ .xlsx หรือ .xls</li>
  <li>ไฟล์มีขนาดเกิน 10MB</li>
</ul>
<p><strong>วิธีแก้:</strong></p>
<ul>
  <li>ตรวจสอบนามสกุลไฟล์</li>
  <li>ลดขนาดไฟล์หรือแบ่งเป็นหลายไฟล์</li>
</ul>

<h4>2. ข้อมูลไม่ถูก Import</h4>
<p><strong>สาเหตุ:</strong></p>
<ul>
  <li>ไม่ได้ Map คอลัมน์</li>
  <li>ข้อมูลไม่ผ่าน Validation</li>
</ul>
<p><strong>วิธีแก้:</strong></p>
<ul>
  <li>ตรวจสอบว่าได้ Map คอลัมน์อย่างน้อย 1 คอลัมน์</li>
  <li>ดู Error messages ในหน้า Preview</li>
  <li>เลือก "Skip error rows" หากต้องการ Import เฉพาะข้อมูลที่ถูกต้อง</li>
</ul>

<h4>3. Email หรือ Phone ไม่ถูกต้อง</h4>
<p><strong>สาเหตุ:</strong></p>
<ul>
  <li>รูปแบบไม่ถูกต้อง</li>
  <li>มีตัวอักษรพิเศษที่ไม่ถูกต้อง</li>
</ul>
<p><strong>วิธีแก้:</strong></p>
<ul>
  <li>ตรวจสอบรูปแบบ Email (ต้องมี @ และ .)</li>
  <li>ตรวจสอบรูปแบบ Phone (ต้องมีตัวเลขอย่างน้อย 7 หลัก)</li>
</ul>

<h4>4. การ Split ไม่ทำงาน</h4>
<p><strong>สาเหตุ:</strong></p>
<ul>
  <li>เลือก Split Type ไม่ถูกต้อง</li>
  <li>Custom Separator ไม่ตรงกับข้อมูล</li>
</ul>
<p><strong>วิธีแก้:</strong></p>
<ul>
  <li>ตรวจสอบตัวอย่างข้อมูลใน Preview</li>
  <li>ลองเปลี่ยน Split Type</li>
  <li>ตรวจสอบ Custom Separator ว่าตรงกับข้อมูลหรือไม่</li>
</ul>

<hr />

<h2>คำแนะนำเพิ่มเติม</h2>

<h3>1. การเตรียมไฟล์ Excel</h3>
<ul>
  <li>✅ ใช้แถวแรกเป็น Header</li>
  <li>✅ ใช้ชื่อคอลัมน์ที่ชัดเจน</li>
  <li>✅ ตรวจสอบข้อมูลให้ถูกต้องก่อน Import</li>
  <li>✅ หลีกเลี่ยงเซลล์ว่างในแถวข้อมูล</li>
</ul>

<h3>2. การ Map ที่ดี</h3>
<ul>
  <li>✅ ใช้ Auto-detection เป็นจุดเริ่มต้น</li>
  <li>✅ ตรวจสอบตัวอย่างข้อมูลก่อน Import</li>
  <li>✅ Map ฟิลด์ที่ Required ก่อน</li>
</ul>

<h3>3. การ Import ข้อมูลจำนวนมาก</h3>
<ul>
  <li>✅ แบ่งไฟล์เป็นหลายไฟล์เล็กๆ หากข้อมูลมาก</li>
  <li>✅ ใช้ Row Range Selection เพื่อ Import ทีละส่วน</li>
  <li>✅ ตรวจสอบ Preview ก่อน Import จริง</li>
</ul>

<hr />

<h2>สรุป</h2>
<p>ระบบ Import Excel มีฟีเจอร์ที่ช่วยให้การนำเข้าข้อมูลเป็นไปอย่างรวดเร็วและมีประสิทธิภาพ:</p>
<ul>
  <li>✅ <strong>Auto-detection</strong>: จับคู่คอลัมน์อัตโนมัติ</li>
  <li>✅ <strong>Multi-field Mapping</strong>: Map หลายฟิลด์จากคอลัมน์เดียว</li>
  <li>✅ <strong>Multiple Values</strong>: รองรับ Email และ Phone หลายค่า</li>
  <li>✅ <strong>Validation</strong>: ตรวจสอบความถูกต้องก่อน Import</li>
  <li>✅ <strong>Preview</strong>: ดูตัวอย่างก่อน Import จริง</li>
</ul>
<p>หากมีคำถามหรือต้องการความช่วยเหลือเพิ่มเติม กรุณาติดต่อทีมพัฒนา</p>

<p><em><strong>อัปเดตล่าสุด</strong>: 6 ธันวาคม 2025</em></p>
`;

export default ExcelImportHelpModal;

